// SanMar color name → hex override map.
//
// Why this exists: the EPDD feed includes a `swatch` image URL per color,
// but most of those URLs now 302-redirect to ImageNotAvailable.jpg, so the
// modal/card color circles all rendered as the same broken-image. The
// hex values shipped in the feed are also unreliable for many colors
// (e.g. Graphite → #999999 generic gray). This map gives accurate visible
// colors for the most common SanMar names. Add more as needed.
//
// Names are normalized lowercase, trimmed, with extra whitespace squashed.
// For two-tone names (e.g. "Black/ White") we look up the part before the
// slash. "Heather" variants fall back to the base color, slightly washed.

const COLOR_MAP = {
  // Blacks
  'black': '#0d0d0d',
  'deep black': '#000000',
  'jet black': '#0a0a0a',
  'tnf black': '#0d0d0d',
  'blacktop': '#1d1d1d',
  'black solid': '#0d0d0d',

  // Whites / off-white / neutrals
  'white': '#fafafa',
  'natural': '#e8dcc8',
  'cream': '#f4ead5',
  'ivory': '#f5f1e6',
  'bone': '#e8e0cd',
  'sand': '#dcc9a8',
  'stone': '#c9bfae',
  'khaki': '#b59e75',
  'tan': '#c9a880',
  'desert sand': '#d2b48c',

  // Greys
  'grey': '#7d7d7d',
  'gray': '#7d7d7d',
  'silver': '#c4c4c4',
  'ash': '#c0bcb6',
  'charcoal': '#3a3a3a',
  'iron grey': '#4a4d4f',
  'iron gray': '#4a4d4f',
  'graphite': '#3f4145',
  'storm grey': '#6b7785',
  'storm gray': '#6b7785',
  'gusty grey': '#8a8d92',
  'gusty gray': '#8a8d92',
  'grey steel': '#5e6266',
  'gray steel': '#5e6266',
  'anthracite': '#2f3031',
  'rainstorm grey': '#6c7783',
  'rainstorm gray': '#6c7783',
  'medium grey': '#888888',
  'medium gray': '#888888',
  'light grey': '#bdbdbd',
  'light gray': '#bdbdbd',
  'dark grey': '#4a4a4a',
  'dark gray': '#4a4a4a',

  // Navies / blues
  'navy': '#1b2a4a',
  'true navy': '#1b2a4a',
  'deep navy': '#0e1b3d',
  'midnight navy': '#0d1733',
  'new navy': '#1d2c52',
  'night navy': '#10182e',
  'river blue navy': '#1a2c4d',
  'royal': '#1f3a8a',
  'true royal': '#1f3a8a',
  'royal blue': '#1f3a8a',
  'sapphire': '#0f4d92',
  'carolina blue': '#7ba4db',
  'light blue': '#a8c8e0',
  'sky': '#9fc7e6',
  'sky blue': '#9fc7e6',
  'aquatic blue': '#3d9bbc',
  'turquoise': '#3fbcb1',
  'teal': '#1f6f74',
  'aqua': '#33bfb1',

  // Reds / pinks
  'red': '#c8102e',
  'true red': '#c8102e',
  'rich red': '#a8112b',
  'deep red': '#8b1a26',
  'cardinal': '#9b1c2c',
  'maroon': '#5a0a18',
  'burgundy': '#5d0a17',
  'wine': '#5e1224',
  'pink': '#f8a3bc',
  'neon pink': '#ff5d96',
  'pink raspberry': '#c91f5a',
  'hot pink': '#ee2a7b',
  'coral': '#ff6f61',

  // Greens
  'kelly': '#0fa15e',
  'kelly green': '#0fa15e',
  'forest green': '#1a4d2e',
  'dark green': '#1f4d33',
  'hunter green': '#1d3b2a',
  'olive': '#5b6a3a',
  'olive drab': '#535b3c',
  'military green': '#4b5320',
  'army green': '#4b5320',
  'lime': '#a4d233',
  'safety green': '#bce300',
  'mint': '#a4d4ad',

  // Oranges / yellows / golds
  'orange': '#ff7a17',
  'tex orange': '#f56500',
  'safety orange': '#ff5800',
  'burnt orange': '#bf5a1c',
  'yellow': '#fce300',
  'gold': '#d4a017',
  'old gold': '#a08334',
  'athletic gold': '#dfb432',

  // Purples / browns
  'purple': '#582c83',
  'team purple': '#4d2c7c',
  'brown': '#5b3a1c',
  'dark chocolate': '#3a261b',
  'carhartt brown': '#5a3b1f',
  'mocha': '#7a4c33',

  // === Comfort Colors palette (garment-dyed) ===
  'blue jean':       '#7a8da0',
  'berry':           '#7d2554',
  'bottle blue':     '#163b4a',
  'brick':           '#7c3a26',
  'butter':          '#f4dc91',
  'chalky mint':     '#bce3cb',
  'chambray':        '#7393b1',
  'charcoal grey':   '#3f4042',
  'chili':           '#a8322a',
  'choco brown':     '#4a2e22',
  'chocolate':       '#4a2e22',
  'citrus':          '#cee63a',
  'cornflower':      '#7c97c9',
  'crimson':         '#8a1f2c',
  'crunchberry':     '#d33d63',
  'crystal':         '#cfd9d9',
  'cumin':           '#b07a3a',
  'denim':           '#465d7a',
  'dusk':            '#8a7a85',
  'espresso':        '#3a241d',
  'flo blue':        '#3a7fa6',
  'granite':         '#7a787a',
  'grass':           '#5fa14a',
  'hemp':            '#b1a187',
  'ice blue':        '#bcd4e3',
  'island reef':     '#7ccab2',
  'light green':     '#b1cd9d',
  'light pink':      '#f5cdd4',
  'mango':           '#f29642',
  'melon':           '#f47b6b',
  'midnight':        '#1c2538',
  'moss':            '#73774a',
  'mustard':         '#b8901f',
  'neon green':      '#5fff70',
  'neon yellow':     '#e7ff42',
  'pacific blue':    '#1f6fa6',
  'pebble':          '#bdb3a1',
  'pepper':          '#3b3a3a',
  'periwinkle':      '#8b9bd2',
  'pomegranate':     '#b1283a',
  'putty':           '#cfb98c',
  'royal caribe':    '#2e6db0',
  'rose quartz':     '#e4bdc6',
  'sandstone':       '#cdb892',
  'seafoam':         '#a8d6c5',
  'seaglass':        '#b9d6c6',
  'sunflower':       '#f3c623',
  'terracotta':      '#b25c44',
  'tropical pink':   '#f5648a',
  'topaz blue':      '#3aa8c5',
  'watermelon':      '#e94e6a',
  'yam':             '#c4622a',

  // === Port Authority / Sport-Tek common colors not covered above ===
  'aquamarine':         '#3fb5a8',
  'aquatic':            '#3d9bbc',
  'atlantic blue':      '#1f5d8c',
  'bright kelly green': '#0fa15e',
  'coastal blue':       '#2e6f9a',
  'coral splash':       '#ff7d6e',
  'deep forest green':  '#1c3a2a',
  'deep smoke':         '#4a5358',
  'dress blue navy':    '#1b2a4a',
  'dusty blue':         '#7b9bb3',
  'gravel':             '#7a7770',
  'lime shock':         '#bce300',
  'magnet':             '#5e6266',
  'olympic gold':       '#dfb432',
  'pink azalea':        '#ee5d8c',
  'pink blossom':       '#f5b7c5',
  'pulse':              '#5da9c0',
  'safety yellow':      '#e8e421',
  'sangria':            '#7d1c3a',
  'sport black':        '#0d0d0d',
  'sunburst yellow':    '#fbcd1b',
  'tropic blue':        '#3da3d9',
  'true blue':          '#2c5aa0',
  'true purple':        '#582c83',
};

// Heuristic offsets for compound names. Applied after direct lookup misses.
function normalize(name) {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    // SanMar uses "Black/ White" with a stray space; collapse the slash side
    .replace(/\s*\/\s*/g, '/');
}

// Mix two #rrggbb hexes by `t` (0..1). t=0 → a, t=1 → b.
function mix(a, b, t) {
  const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
  const ar = (pa >> 16) & 0xff, ag = (pa >> 8) & 0xff, ab = pa & 0xff;
  const br = (pb >> 16) & 0xff, bg = (pb >> 8) & 0xff, bb = pb & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return '#' + [r, g, bl].map(x => x.toString(16).padStart(2, '0')).join('');
}

function lookupBase(name) {
  // Direct match first
  if (COLOR_MAP[name]) return COLOR_MAP[name];

  // Strip common modifier prefixes
  const stripped = name
    .replace(/^(deep|rich|true|new|dark|light|bright|neon|safety|athletic|heather|heathered|medium)\s+/, '')
    .trim();
  if (stripped !== name && COLOR_MAP[stripped]) return COLOR_MAP[stripped];

  // Try base after the modifier (last word)
  const words = name.split(' ').filter(Boolean);
  const last = words[words.length - 1];
  if (last && COLOR_MAP[last]) return COLOR_MAP[last];

  return null;
}

// Public: resolve a SanMar color name to a hex string.
// `fallback` is the original `c.hex` from the data (used if nothing matches).
export function resolveColorHex(name, fallback) {
  const norm = normalize(name);

  // Two-tone: take the part before the slash
  const primary = norm.split('/')[0].trim();

  // Direct or base lookup
  let hex = lookupBase(primary);

  // Heather: lighten the base toward a soft grey
  if (hex && /\bheather(ed)?\b/.test(norm)) {
    hex = mix(hex, '#bdb6b2', 0.35);
  }

  return hex || (fallback && /^#[0-9a-fA-F]{6}$/.test(fallback) ? fallback : '#cccccc');
}

export default resolveColorHex;
